<script setup lang="ts">
import { computed, provide, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import NoticesMenuCardCollection from '@/components/collections/NoticesMenuCardCollection.vue';
import NoticesHeader from '@/components/headers/NoticesHeader.vue';
import NoticesTabWinHeader from '@/components/headers/NoticesTabWinHeader.vue';
import SimpleLoader from '@/components/loaders/SimpleLoader.vue';
import SmartNoticesTabs from '@/components/tabs/SmartNoticesTabs.vue';
import NoticesEmptyText from '@/components/texts/NoticesEmptyText.vue';
import NoticesErrorText from '@/components/texts/NoticesErrorText.vue';
import SmartNoticesVWindow from '@/components/vWindows/SmartNoticesVWindow.vue';
import { useStore } from '@/store';
import {
  noticesMarkAllAsReadKey,
  noticesOpenItemKey,
  noticesToggleItemReadKey,
  noticesToggleReadFilterKey,
} from '@/constants/injectionKeys';
import * as routeNames from '@/constants/routeNames';

export interface Props {
  closeMenu: () => void;
}
const props = withDefaults(defineProps<Props>(), {});

const store = useStore();

const notifications = computed(
  () => store.state.moduleNotifications.notifications,
);

const items = computed(() => {
  return notifications.value.items_shown.map((item) => {
    return {
      title: item.verb,
      description: item.description,
      timesince: item.timesince,
      timestamp: item.timestamp,
      level: item.level,
      isRead: !item.unread,
      id: item.id,
    };
  });
});

const itemsAllKeys = computed(() => {
  return Object.keys(notifications.value.items_all);
});

const scrollEl = ref<HTMLElement | null>(null);

const loaderNextPage = ref(false);

const route = useRoute();
const router = useRouter();

function scrollToTop(): void {
  scrollEl.value?.scrollTo({ top: 0 });
}

async function handleScroll(event: Event): Promise<void> {
  if (event.target == null) {
    return;
  }
  const { scrollTop, offsetHeight, scrollHeight } = event.target as HTMLElement;
  if (scrollTop + offsetHeight >= scrollHeight - 400) {
    if (!loaderNextPage.value) {
      loaderNextPage.value = true;
      await store.dispatch('moduleNotifications/getNextPage');
      loaderNextPage.value = false;
    }
  }
}

async function toggleReadFilter(): Promise<void> {
  scrollToTop();
  await store.dispatch(
    'moduleNotifications/setNotificationsUnreadFilter',
    !notifications.value.unread_filter,
  );
}

async function chooseTab(category: string): Promise<void> {
  scrollToTop();
  void store.dispatch('moduleNotifications/setNotificationsCategory', category);
}

async function markAllAsRead(): Promise<void> {
  await store.dispatch('moduleNotifications/setNotificationsAllAsRead');
}

async function toggleItemRead(notificationId: number): Promise<void> {
  const notification = notifications.value.items_shown.find(
    (notification) => notificationId === notification.id,
  );
  if (notification == null) {
    return;
  }
  if (notification.unread) {
    await store.dispatch(
      'moduleNotifications/setNotificationAsRead',
      notification.id,
    );
  } else {
    await store.dispatch(
      'moduleNotifications/setNotificationAsUnread',
      notification.id,
    );
  }
}

// TODO refactor
async function openItem(notificationId: number): Promise<void> {
  const notification = notifications.value.items_shown.find(
    (notification) => notificationId === notification.id,
  );
  if (notification == null) {
    return;
  }
  // Tickets – Zammad ticket page by `source_target_id`
  // TODO
  // @ts-expect-error
  const ids = String(notification.data.ids.join(','));
  // Check router match + current route
  if (
    // TODO
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    notification.data?.name &&
    // TODO
    // @ts-expect-error
    Object.keys(routeNames).includes(notification.data.name) &&
    ids !== route.params.id
  ) {
    const data = {
      // TODO
      // @ts-expect-error
      name: routeNames[notification.data.name],
      // TODO
      // @ts-expect-error
      ...(notification.data.name === 'SEARCH'
        ? { query: { job_id: ids } }
        : { params: { id: ids } }),
    };
    await router.push(data);
  }
  await store.dispatch(
    'moduleNotifications/setNotificationAsRead',
    notification.id,
  );
  props.closeMenu();
}

provide(noticesToggleReadFilterKey, toggleReadFilter);
provide(noticesMarkAllAsReadKey, markAllAsRead);
provide(noticesOpenItemKey, openItem);
provide(noticesToggleItemReadKey, toggleItemRead);
</script>

<template>
  <v-sheet :class="$style.noticesMenuCard">
    <NoticesHeader
      :is-switch-disabled="!!notifications.error"
      :is-read-filter="notifications.unread_filter"
    />

    <template
      v-if="
        !notifications.loading && !notifications.error && itemsAllKeys.length
      "
    >
      <SmartNoticesTabs />
      <NoticesTabWinHeader
        :is-mark-all-disabled="notifications.counter_unread === 0"
      />
    </template>

    <template v-if="notifications.loading">
      <SimpleLoader :class="$style.emptyLoader" />
    </template>
    <template v-else-if="notifications.error">
      <NoticesErrorText :error="notifications.error" />
    </template>
    <template v-else>
      <div
        ref="scrollEl"
        :class="[$style.scroll, items.length === 0 ? 'overflow-y-hidden' : '']"
        @scroll="handleScroll"
      >
        <template v-if="itemsAllKeys.length">
          <SmartNoticesVWindow :choose-tab="chooseTab">
            <template v-if="items.length > 0">
              <NoticesMenuCardCollection
                :items="items"
                :loading-ids="notifications.loadingIds"
              />
            </template>
            <template v-else>
              <NoticesEmptyText />
            </template>
            <SimpleLoader v-if="loaderNextPage" />
          </SmartNoticesVWindow>
        </template>
        <template v-else>
          <NoticesEmptyText />
        </template>
      </div>
    </template>
  </v-sheet>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
.noticesMenuCard {
  width: 515px;
}
  .emptyLoader {
    min-height: 83px;
  }
  .scroll {
    max-height: 480px;
    overflow-y: scroll;
  }
</style>
